import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type PageWidth = 'default' | 'medium' | 'narrow' | 'full';

/**
 * Content max-width per page kind. The single place the app's page widths are
 * decided — change here, every page follows.
 */
const WIDTH: Record<PageWidth, string> = {
  default: 'max-w-[1400px]', // standard list/detail pages
  medium: 'max-w-[1200px]', // account / billing (narrower reading width)
  narrow: 'max-w-2xl', // focused forms / settings
  full: 'max-w-none', // full-bleed (e.g. the network graph)
};

interface PageContainerProps {
  children: ReactNode;
  /** Content width. Defaults to the app-wide standard. */
  width?: PageWidth;
  /** Extra classes for the rare one-off (e.g. a page that manages its own padding). */
  className?: string;
}

/**
 * The standard page shell: consistent horizontal gutters, vertical rhythm and a
 * centered max-width. This is the single source of truth for page-level
 * spacing — pair it with `<PageHeader>`. Having this primitive is what prevents
 * the per-page className drift (7xl vs 1400 vs 1600) it replaces.
 */
export function PageContainer({ children, width = 'default', className }: PageContainerProps) {
  return (
    <div className={cn('mx-auto w-full px-4 md:px-6 py-5 md:py-6', WIDTH[width], className)}>
      {children}
    </div>
  );
}

export default PageContainer;
